import React, { useMemo } from 'react';
import { Card, Table, Tag, Typography, Alert } from 'antd';

const { Text } = Typography;

interface ViscosityItem {
  name: string;
  prob_mean?: number;
  prob_std?: number;
  viscosity_class?: number;
  viscosity_label?: string;
}

interface ViscosityResultProps {
  data: any;
  title?: string;
}

function normalizeResults(payload: any): ViscosityItem[] {
  if (!payload) return [];

  // 1) 直接对象
  if (payload && typeof payload === 'object' && 'name' in payload && ('viscosity_class' in payload || 'viscosity_label' in payload)) {
    return [payload as ViscosityItem];
  }

  // 2) 统一任务格式: { results: [...] }
  if (Array.isArray(payload?.results)) {
    const directMatches = payload.results.filter((it: any) => it && typeof it === 'object' && ('viscosity_class' in it || 'viscosity_label' in it));
    if (directMatches.length > 0) return directMatches as ViscosityItem[];

    // 嵌套 result
    const nested = payload.results.flatMap((item: any) => normalizeResults(item?.result));
    if (nested.length > 0) return nested;
  }

  // 3) 直接数组
  if (Array.isArray(payload)) {
    const list = payload.filter((it: any) => it && typeof it === 'object' && ('viscosity_class' in it || 'viscosity_label' in it));
    if (list.length > 0) return list as ViscosityItem[];
  }

  return [];
}

const ViscosityResult: React.FC<ViscosityResultProps> = ({ data, title = '抗体粘度预测结果' }) => {
  const results = useMemo(() => normalizeResults(data), [data]);

  if (!data) {
    return <Alert type="info" message="暂无结果数据" showIcon />;
  }

  if (results.length === 0) {
    return (
      <Alert
        type="warning"
        message="未识别到抗体粘度结果"
        description="任务可能仍在运行，或返回格式未匹配。"
        showIcon
      />
    );
  }

  const columns = [
    { title: '名称', dataIndex: 'name', key: 'name', width: 180 },
    { title: '类别', dataIndex: 'viscosity_label', key: 'viscosity_label', render: (label: string) => (
        <Tag color={label?.toLowerCase().includes('high') ? 'red' : label?.toLowerCase().includes('low') ? 'green' : 'blue'}>
          {label || '未知'}
        </Tag>
      )
    },
    { title: '类别编号', dataIndex: 'viscosity_class', key: 'viscosity_class', width: 100 },
    { title: '概率均值', dataIndex: 'prob_mean', key: 'prob_mean', width: 120, render: (v: number) => v != null ? v.toFixed(4) : '-' },
    { title: '概率标准差', dataIndex: 'prob_std', key: 'prob_std', width: 120, render: (v: number) => v != null ? v.toFixed(4) : '-' }
  ];

  return (
    <Card>
      <div style={{ marginBottom: 12 }}>
        <Text strong>{title}</Text>
        <Text type="secondary" style={{ marginLeft: 8 }}>共 {results.length} 条</Text>
      </div>
      <Table
        size="small"
        rowKey={(r) => `${r.name}-${r.viscosity_label || r.viscosity_class || ''}`}
        dataSource={results}
        columns={columns as any}
        pagination={{ pageSize: 10, showSizeChanger: false }}
      />
    </Card>
  );
};

export default ViscosityResult;


